import React, { useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonPopover } from '@ionic/react';
import { StarIcon, StarOutlineIcon } from '../theme/Icons';
import { Text } from '../components/Text';
import { useIfLoggedIn } from '../utils/useIfLoggedIn';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IResource } from './IResource';

const useStyles = createUseStyles({
    ratingStars: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    },

    ratingStarsOverlay: {
        backgroundColor: 'white',
        opacity: 0.75,
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 1
    },

    popover: {
        '--width': 'fit-content',
        '--offset-y': '-10px'
    },

    popoverContent: {
        padding: 8
    },

    popoverStar: {
        // referenced using $popoverStar below
        margin: 0,

        '&::part(native)': {
            width: 50,
            height: 50
        }
    },

    popoverStarFilled: {
        // referenced using $popoverStarFilled below
    },

    popoverStarOutline: {
        // referenced using $popoverStarOutline below
    },

    popoverStars: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',

        // on hover, fill all stars by default
        '&:hover': {
            '& $popoverStarFilled': { display: 'inline !important' },
            '& $popoverStarOutline': { display: 'none !important' }
        },

        // then unfill those to the right of the hover
        '& $popoverStar:hover ~ $popoverStar': {
            '& $popoverStarFilled': { display: 'none !important' },
            '& $popoverStarOutline': { display: 'inline !important' }
        }
    }
});

export interface IResourceRatingProps {
    resource: IResource,
    onRate: (rating: number | undefined) => void,
    trackingFrom: 'List' | 'Details',
    buttonSize?: 'small'
}

export const ResourceRating: React.FC<IResourceRatingProps> = ({ resource, onRate, trackingFrom, buttonSize }) => {
    const { rating: { avgRating = 0, myRating = 0, ratingCount } } = resource;

    const classes = useStyles();

    const mixpanel = useInstance(MixPanelEventEmitter);

    const popover = useRef<HTMLIonPopoverElement>(null);
    const [expanded, setExpanded] = useState<boolean>(false);

    const ifLoggedIn = useIfLoggedIn();

    const openPopover = (e: React.MouseEvent) => {
        const track = (authenticated: boolean) => {
            mixpanel.track('Resource Rating Initiated', () => ({
                Name: resource.name,
                Category: resource.category,
                From: trackingFrom,
                Anonymous: !authenticated
            }));
        };
        ifLoggedIn({
            delegate: () => {
                track(true);
                popover.current!.event = e;
                setExpanded(true);
            },
            unathenticatedDelegate: () => {
                track(false);
            },
            message: 'Please sign in or create an account to rate resources.',
            redirect: '/resources',
            trackingFrom: 'Resources'
        });
    };

    const onClickStar = (rating: number) => {
        // if they click again on the selected rating, toggle it off and
        // close popup immediately (otherwise the hover effect is confusing)
        const removed = (rating === myRating);
        const delay = (removed ? 0 : 750);

        const oldRating = myRating;
        const newRating = (removed ? undefined : rating);

        onRate(newRating);

        mixpanel.track(`Resource Rating ${removed ? 'Removed' : oldRating ? 'Updated' : 'Added'}`, () => ({
            Name: resource.name,
            Category: resource.category,
            Rating: removed ? oldRating : newRating,
            From: trackingFrom
        }));

        setTimeout(() => {
            setExpanded(false);
        }, delay);
    };

    return (
        <>
            <div style={{display: 'flex', width: '100%', alignItems: 'center', gap: 8}}>

                {!avgRating &&
                    <>
                        <StarOutlineIcon color="theme-yellow" />
                        <div style={{flexGrow: 1, overflow: 'auto'}}>
                            <Text ellipsize><i>Be the first to rate this!</i></Text>
                        </div>
                    </>
                }

                {Boolean(avgRating) &&
                    <>
                        <div><Text>{avgRating.toFixed(1)}</Text></div>
                        <div className={classes.ratingStars}>
                            {[1, 2, 3, 4, 5].map(rating => (
                                <StarIcon key={`rating-${rating}`} color="theme-yellow" />
                            ))}
                            <div className={classes.ratingStarsOverlay} style={{ width: `${avgRating ? 100 - avgRating * 20 : 100}%` }} />
                        </div>
                        <div style={{flexGrow: 1, overflow: 'auto'}}>
                            <Text>({ratingCount})</Text>
                        </div>
                    </>
                }

                <IonButton data-type="rate-button" mode="ios" size={buttonSize} color="light" onClick={openPopover}>{myRating ? 'Update Rating' : 'Add Rating'}</IonButton>

            </div>

            <IonPopover
                side="top"
                alignment="end"
                ref={popover}
                isOpen={expanded}
                showBackdrop={false}
                className={classes.popover}
                data-type="ratings-popover"
                onDidDismiss={() => setExpanded(false)}

                // need to set keepContentsMounted, otherwise content that expands popover
                // beyond minWidth sometimes pushes it off the right side of the screen
                // (see https://github.com/ionic-team/ionic-framework/issues/24716)
                keepContentsMounted
            >
                <div className={classes.popoverContent}>
                    <div className={classes.popoverStars}>
                        {[1, 2, 3, 4, 5].map(rating => (
                            // render both filled and unfillled, so we can switch between them on hover using css
                            <IonButton key={`rating-${rating}`} fill="clear" shape="round" className={classes.popoverStar} onClick={() => onClickStar(rating)}>
                                <span className={classes.popoverStarFilled} style={{display: (myRating < rating) ? 'none' : 'inline'}}>
                                    <StarIcon color="theme-yellow" size="xlarge" />
                                </span>
                                <span className={classes.popoverStarOutline} style={{display: (myRating >= rating) ? 'none' : 'inline'}}>
                                    <StarOutlineIcon color="medium" size="xlarge" />
                                </span>
                            </IonButton>
                        ))}
                    </div>
                </div>
            </IonPopover>
        </>
    );
};
